import { ContextMenu, useSnackbar } from '@automata/ui';
import { logger } from '@automata/utils';
import {
  useGetTransportConfig,
  useRenameWorkflow,
  useSaveWorkflow,
  useValidateWorkflow,
} from '@automata/wfb-api';
import {
  PlannerOptionsInput,
  RenameWorkflowInput,
  WorkflowValidationResult,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
// import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
// import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
// import EditLocationOutlinedIcon from '@mui/icons-material/EditLocationOutlined';
// import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
// import Chip from '../ChipWFB';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useMediaQuery, useTheme } from '@mui/material';
import Divider from '@mui/material/Divider';
import Stack, { StackProps } from '@mui/material/Stack';
import { useRouter } from 'next/router';
import React, { useCallback, useRef, useState } from 'react';

import { DeleteWorkflowModal } from './DeleteWorkflowModal';
import { DeveloperToolsMenu } from './DeveloperToolsMenu';
import { DuplicateWorkflowModal } from './DuplicateWorkflowModal';
import IconButton from './IconButton';
import { PublishWorkflowModal } from './PublishWorkflowModal';
import WorkflowNameInput from './WorkflowNameInput';
import BuildPreviewTabs from '../../components/BuildPreviewTabs';
import { selectWorkflowIsSaved } from '../../features/ui/selectors';
import { setWorkflowIsSaved } from '../../features/ui/uiSlice';
import {
  selectWorkcellTransportConfigId,
  selectWorkcellTransportConfigType,
  selectWorkcellTransportConfigVersion,
  selectWorkflowName,
} from '../../features/workflow/selectors';
import {
  setWorkflowMetadata,
  setWorkflowName,
} from '../../features/workflow/workflowSlice';
import { selectWorkflowJsonToSave } from '../../features/workflowJsonSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { TabId } from '../../types';
import useIsPlanWorkflowModalOpen from '../PreviewPanel/IsPlanWorkflowModalOpenContext/useIsPlanWorkflowModalOpen';
import { PlanWorkflowModal } from '../PreviewPanel/PlanWorkflowModal';
import { ValidateWorkflowModal } from '../WorkflowValidationModal';

export type Props = {
  onClickExpandIcon: () => void;
  workflowId?: string;
  isExistingWorkflow: boolean;
  activeTabId: TabId;
  onChangeTab: (tabId: TabId) => void;
  saveLockRef: React.MutableRefObject<boolean>;
};

const vertDivider = (
  <Divider variant="middle" orientation="vertical" flexItem />
);

const baseContainerConfig: StackProps = {
  direction: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 1,
};

export const WorkflowInfoToolbar = ({
  onClickExpandIcon,
  workflowId,
  isExistingWorkflow,
  activeTabId,
  onChangeTab,
  saveLockRef,
}: Props): JSX.Element => {
  const dispatch = useAppDispatch();
  const workflowToSave = useAppSelector(selectWorkflowJsonToSave);
  const isWorkflowSaved = useAppSelector(selectWorkflowIsSaved);
  const workflowName = useAppSelector(selectWorkflowName);
  const transportConfigId = useAppSelector(selectWorkcellTransportConfigId);
  const transportConfigType = useAppSelector(selectWorkcellTransportConfigType);
  const transportConfigVersion = useAppSelector(
    selectWorkcellTransportConfigVersion
  );

  const [isDeleteWorkflowOpen, setIsDeleteWorkflowOpen] = useState(false);
  const [isPublishWorkflowOpen, setIsPublishWorkflowOpen] = useState(false);
  const [isDuplicateWorkflowOpen, setIsDuplicateWorkflowOpen] = useState(false);
  const [isValidateWorkflowOpen, setIsValidateWorkflowOpen] = useState(false);
  const [isPlanWorkflowModalOpen, setIsPlanWorkflowModalOpen] =
    useIsPlanWorkflowModalOpen();

  const saveToFetchMaxBatchesBeforePublish = useRef(false);
  const saveToFetchMaxBatchesBeforeValidtion = useRef(false);
  const [validationResult, setValidationResult] =
    useState<WorkflowValidationResult | null>(null);

  const theme = useTheme();
  const largerViewport = useMediaQuery(theme.breakpoints.up('sm'));

  const { enqueueSnackbar } = useSnackbar();

  const { mutate: renameWorkflowMutate } = useRenameWorkflow();

  const router = useRouter();

  // logic for saving workflow, used only in handleClickSaveIcon
  const { saveWorkflow } = useSaveWorkflow({
    onSuccess(data) {
      saveLockRef.current = false; // Release lock
      if (!isExistingWorkflow) {
        // we're saving the workflow for the first time.
        // we inject the newly created workflow id into the url
        // and set an 'initialSave' query param to indicate
        // to the CanvasApp that it should
        // continue using the same redux store.
        router.replace({
          query: { ...router.query, initialSave: true, id: data.id },
        });
      }
      dispatch(setWorkflowMetadata(data));

      if (saveToFetchMaxBatchesBeforePublish.current) {
        setIsPublishWorkflowOpen(true);
        saveToFetchMaxBatchesBeforePublish.current = false;
      }
      if (saveToFetchMaxBatchesBeforeValidtion.current) {
        setIsPlanWorkflowModalOpen(true);
        saveToFetchMaxBatchesBeforeValidtion.current = false;
      } else {
        // dont show the confirmation message if we're just fetching max batches
        enqueueSnackbar('Workflow saved successfully', { variant: 'success' });
      }
    },
    onError(error) {
      saveLockRef.current = false; // Release lock
      logger.error(error);
      enqueueSnackbar('Failed to save workflow', { variant: 'error' });
      dispatch(setWorkflowIsSaved(false));
    },
  });

  // TODO(AL, 10/07/2024): Need to handle error, loading states etc.
  const handleSaveWorkflow = useCallback(() => {
    if (saveLockRef.current) {
      enqueueSnackbar('Save already in progress', { variant: 'warning' });
      return;
    }
    saveLockRef.current = true; // Set lock
    saveWorkflow(workflowToSave, workflowId);
    dispatch(setWorkflowIsSaved(true));
  }, [
    dispatch,
    saveWorkflow,
    workflowId,
    workflowToSave,
    saveLockRef,
    enqueueSnackbar,
  ]);

  const handleEditWorkflowName = useCallback(
    (name: string) => {
      const isWorkflowNameEmpty = name.trim() === '';
      if (!isWorkflowNameEmpty) {
        const prevName = workflowName;
        dispatch(setWorkflowName(name));

        if (isExistingWorkflow && workflowId) {
          const payload: RenameWorkflowInput = { name };
          renameWorkflowMutate(
            {
              body: payload,
              pathParams: { workflowId },
            },
            {
              onSuccess: () => {
                enqueueSnackbar('Workflow renamed', { variant: 'success' });
                dispatch(setWorkflowIsSaved(true));
              },
              onError: (error) => {
                enqueueSnackbar(`Cannot rename workflow: ${error}`, {
                  variant: 'error',
                });
                dispatch(setWorkflowName(prevName));
                dispatch(setWorkflowIsSaved(true));
              },
            }
          );
        }
      }
    },
    [
      dispatch,
      isExistingWorkflow,
      workflowId,
      renameWorkflowMutate,
      workflowName,
      enqueueSnackbar,
    ]
  );

  // NOTE(IL, 2024-11-22): this was used to drive `showTimeEstimateBadge`, hidden in WBP-386
  // const timeEstimatesAreMissing = workflowConfigHasWarning(
  //   workflowJsonPreview, // this was the output of selectWorkflowJsonToPreview selector
  //   MISSING_TIME_ESTIMATE
  // );

  const {
    mutate: validateWorkflowMutate,
    status: validateWorkflowStatus,
    error: validateWorkflowError,
  } = useValidateWorkflow({
    onMutate: () => {
      setIsValidateWorkflowOpen(true);
    },
    onSuccess: (data) => {
      setValidationResult(data);
    },
  });
  const { data: resolvedTransportConfig } = useGetTransportConfig(
    {
      queryParams: {
        id: transportConfigId ?? '',
        type: transportConfigType ?? 'V1',
        version: transportConfigVersion,
      },
    },
    {
      enabled: Boolean(transportConfigId && transportConfigType),
    }
  );

  const handleValidate = useCallback(
    ({
      batches,
    }: Partial<{
      batches: number;
      optimisationTarget: PlannerOptionsInput['optimisation_target'];
    }>) => {
      setIsPlanWorkflowModalOpen(false);
      const transportConfigForValidation =
        resolvedTransportConfig && transportConfigType
          ? {
              id: resolvedTransportConfig.id,
              type: resolvedTransportConfig.type,
              ...(transportConfigVersion !== undefined
                ? { version: transportConfigVersion }
                : {}),
            }
          : (workflowToSave.workcell as any)?.transport_config;

      validateWorkflowMutate({
        body: {
          ...workflowToSave,
          workcell: {
            ...workflowToSave.workcell,
            workcell_id: transportConfigForValidation
              ? null
              : resolvedTransportConfig?.id ?? transportConfigId,
            transport_config: transportConfigForValidation,
          } as any,
          workflow: {
            ...workflowToSave.workflow,
            batches,
          },
        },
        queryParams: {
          validate_for_execution: true,
          validate_infeasibility: true,
        },
      });
    },
    [
      resolvedTransportConfig,
      setIsPlanWorkflowModalOpen,
      transportConfigType,
      transportConfigVersion,
      transportConfigId,
      validateWorkflowMutate,
      workflowToSave,
    ]
  );

  const handleClickValidate = () => {
    saveToFetchMaxBatchesBeforeValidtion.current = true;
    // We only save here to determine max batches.
    handleSaveWorkflow();
  };

  return (
    <Stack
      {...baseContainerConfig}
      justifyContent="space-between"
      width="100%"
      px={1}
    >
      <Stack {...baseContainerConfig} py={0.5}>
        <Stack
          {...baseContainerConfig}
          justifyContent="space-between"
          flexGrow={1}
        >
          <WorkflowNameInput
            value={workflowName}
            onChange={handleEditWorkflowName}
          />
          <Stack {...baseContainerConfig} justifyContent="flex-end">
            {/* <Chip label="V1" /> */}
            {/* <Chip label="DRAFT" /> */}
            <IconButton
              icon={ExpandMoreIcon}
              onClick={onClickExpandIcon}
              trackLabel="workflowBuilder.infoToolbar.workflowDescriptionToggleButton"
              aria-label="Show/hide workflow description panel"
            />

            {largerViewport && vertDivider}
          </Stack>
        </Stack>

        <Stack
          {...baseContainerConfig}
          justifyContent={largerViewport ? 'flex-start' : 'space-evenly'}
          flexGrow={1}
          bgcolor={!largerViewport ? theme.palette.grey[100] : 'transparent'}
          py={!largerViewport ? 1 : 0}
        >
          {/* <IconButton
            icon={FormatListBulletedIcon}
            trackLabel="workflowBuilder.infoToolbar.runInstructionsButton_unused"
            aria-label="Show/hide run instructions"
          />
          <IconButton
            icon={EditLocationOutlinedIcon}
            trackLabel="workflowBuilder.infoToolbar.labwarePlacementButton_unused"
            aria-label="Show/hide labware placement"
          />
          <IconButton
            icon={AccessTimeOutlinedIcon}
            trackLabel="workflowBuilder.infoToolbar.timingsTableButton_unused"
            aria-label="Show/hide timings table"
            showBadge={showTimeEstimateBadge}
          />

          {vertDivider}

          <IconButton
            icon={LockOpenOutlinedIcon}
            trackLabel="workflowBuilder.infoToolbar.lockEditingButton_unused"
            aria-label="Lock/unlock editing"
          /> */}
          <IconButton
            icon={SaveOutlinedIcon}
            trackLabel="workflowBuilder.infoToolbar.saveWorkflowButton"
            onClick={handleSaveWorkflow}
            aria-label="Save current workflow"
            disabled={isWorkflowSaved}
          />
          <IconButton
            icon={FactCheckOutlinedIcon}
            trackLabel="workflowBuilder.infoToolbar.validateWorkflowButton"
            onClick={handleClickValidate}
            aria-label="Validate current workflow"
          />
          {workflowId !== undefined && (
            <>
              <ContextMenu
                menuOptions={['Delete', 'Publish', 'Duplicate']}
                selectAction={(action, _anchorEl) => {
                  if (action === 'Delete') {
                    setIsDeleteWorkflowOpen(true);
                  }
                  if (action === 'Publish') {
                    // With saveToFetchMaxBatches flag active, the onSuccess
                    // callback of saveWorkflow will open the publish modal
                    saveToFetchMaxBatchesBeforePublish.current = true;
                    handleSaveWorkflow();
                  }
                  if (action === 'Duplicate') {
                    setIsDuplicateWorkflowOpen(true);
                  }
                }}
              />
              <DuplicateWorkflowModal
                key={workflowId}
                open={isDuplicateWorkflowOpen}
                onClose={() => {
                  setIsDuplicateWorkflowOpen(false);
                }}
                workflowName={workflowName}
              />
              <DeleteWorkflowModal
                open={isDeleteWorkflowOpen}
                onClose={() => {
                  setIsDeleteWorkflowOpen(false);
                }}
                workflowId={workflowId}
                workflowName={workflowName}
              />
              {isPublishWorkflowOpen && (
                <PublishWorkflowModal
                  onClose={() => setIsPublishWorkflowOpen(false)}
                  workflowId={workflowId}
                  workflowName={workflowName}
                />
              )}
            </>
          )}
          {isPlanWorkflowModalOpen && (
            <PlanWorkflowModal
              label="Validate"
              workflowName={workflowToSave.name}
              onSubmit={handleValidate}
              onClose={() => {
                setIsPlanWorkflowModalOpen(false);
              }}
            />
          )}
          <ValidateWorkflowModal
            open={isValidateWorkflowOpen}
            onClose={() => setIsValidateWorkflowOpen(false)}
            validationResult={validationResult}
            error={validateWorkflowError}
            status={validateWorkflowStatus}
          />
          <DeveloperToolsMenu />
        </Stack>
      </Stack>

      <BuildPreviewTabs activeTabId={activeTabId} onChangeTab={onChangeTab} />
    </Stack>
  );
};
